import { Star, Camera, Scan, Calendar, Hash, Maximize2 } from 'lucide-react';

export default function SportPhotoInfo({ photo, language = 'pl' }) {
  if (!photo) return null;

  const t = language === 'pl'
    ? { filename: 'Plik', dimensions: 'Wymiary', rating: 'Ocena', aiTags: 'AI Tagi', created: 'Data', burstGroup: 'Seria' }
    : { filename: 'File', dimensions: 'Dimensions', rating: 'Rating', aiTags: 'AI Tags', created: 'Created', burstGroup: 'Burst' };

  const stars = photo.star_rating || 0;
  const aiTags = photo.ai_tags || [];
  const w = photo.variants?.width || 0;
  const h = photo.variants?.height || 0;

  // Count AI tags by type
  const tagCounts = {};
  for (const tag of aiTags) {
    tagCounts[tag.type] = (tagCounts[tag.type] || 0) + 1;
  }

  const tagColors = {
    dog: 'text-blue-400',
    person: 'text-red-400',
    face: 'text-green-400',
    horse: 'text-purple-400',
  };

  return (
    <div className="space-y-4 text-sm">
      {/* Filename */}
      <div>
        <div className="flex items-center gap-1.5 text-white/50 mb-1">
          <Hash className="w-3.5 h-3.5" />
          {t.filename}
        </div>
        <p className="font-mono text-white/90 text-xs break-all">{photo.filename}</p>
      </div>

      {/* Dimensions */}
      {w > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-white/50 mb-1">
            <Maximize2 className="w-3.5 h-3.5" />
            {t.dimensions}
          </div>
          <p className="text-white/90">{w} x {h}</p>
        </div>
      )}

      {/* Star rating */}
      {stars > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-white/50 mb-1">
            <Star className="w-3.5 h-3.5" />
            {t.rating}
          </div>
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map(s => (
              <Star
                key={s}
                className={`w-4 h-4 ${s <= stars ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* AI Tags summary */}
      {aiTags.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-white/50 mb-1">
            <Scan className="w-3.5 h-3.5" />
            {t.aiTags} ({aiTags.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(tagCounts).map(([type, count]) => (
              <span
                key={type}
                className={`text-xs px-2 py-0.5 rounded-full bg-white/10 ${tagColors[type] || 'text-white/70'}`}
              >
                {type} x{count}
              </span>
            ))}
          </div>
          {/* Named tags */}
          {aiTags.filter(t => t.dog_name || t.competitor_name).length > 0 && (
            <div className="mt-2 space-y-1">
              {aiTags.filter(t => t.dog_name).map((tag, i) => (
                <p key={`d${i}`} className="text-xs text-blue-300">
                  {tag.dog_name} ({Math.round((tag.confidence || 0) * 100)}%)
                </p>
              ))}
              {aiTags.filter(t => t.competitor_name).map((tag, i) => (
                <p key={`c${i}`} className="text-xs text-red-300">
                  {tag.competitor_name} ({Math.round((tag.confidence || 0) * 100)}%)
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Created date */}
      {photo.created_at && (
        <div>
          <div className="flex items-center gap-1.5 text-white/50 mb-1">
            <Calendar className="w-3.5 h-3.5" />
            {t.created}
          </div>
          <p className="text-white/90 text-xs">
            {new Date(photo.created_at).toLocaleString(language === 'pl' ? 'pl-PL' : 'en-US')}
          </p>
        </div>
      )}

      {/* Burst group */}
      {photo.burst_group_id && (
        <div>
          <div className="flex items-center gap-1.5 text-white/50 mb-1">
            <Camera className="w-3.5 h-3.5" />
            {t.burstGroup}
          </div>
          <p className="text-white/90 text-xs font-mono">{photo.burst_group_id}</p>
        </div>
      )}
    </div>
  );
}
